import styled, { css } from 'styled-components';
import { DialogContent } from '@/components/ui/dialog';
import type { StatusTone } from './types';
import { AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsContent } from '@/components/ui/tabs';

export const PlaygroundDialog = styled(DialogContent)`
    && {
        display: flex;
        flex-direction: column;
        width: min(1080px, calc(100vw - 64px));
        max-width: 1080px;
        height: min(760px, calc(100dvh - 80px));
        padding: 0;
        gap: 0;
        overflow: hidden;
        background: var(--color-card);
        color: var(--color-foreground);
        border-radius: var(--radius-xl, 12px);
    }
    @media (max-width: 700px) {
        && {
            width: calc(100vw - 24px);
            height: calc(100dvh - 32px);
        }
    }
`;
export const DialogHeading = styled.header`
    padding: var(--space-4, 16px) var(--space-5, 20px);
    padding-right: 52px;
    display: flex;
    align-items: center;
    gap: var(--space-3, 12px);
    > svg {
        color: var(--color-primary);
        padding: var(--space-2, 8px);
        width: 38px;
        height: 38px;
        flex-shrink: 0;
        border-radius: var(--radius-lg, 8px);
        background: var(--color-muted);
    }
    h2 {
        margin: 0;
        font-size: var(--font-size-sm, 14px);
        line-height: 1.5;
    }
    p {
        margin-top: 2px;
        font-size: var(--font-size-xs, 12px);
    }
`;
export const RequestBar = styled.div`
    display: flex;
    align-items: center;
    gap: var(--space-2, 8px);
    margin: 0 var(--space-4, 16px) var(--space-2, 8px);
    padding: var(--space-1, 4px);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg, 8px);
    background: var(--color-muted);
    > code:first-child {
        box-sizing: border-box;
        height: 22px;
        align-items: center;
        line-height: 1;
        padding-top: 0;
        padding-bottom: 0;
    }
    && > button {
        box-sizing: border-box;
        height: 22px;
        min-height: 0;
        padding: 0 var(--space-2, 8px);
        font-size: var(--font-size-xs, 11px);
        line-height: 1;
        gap: var(--space-1, 4px);
    }
    && > button svg {
        width: 12px;
        height: 12px;
    }
`;
export const RequestPath = styled.code`
    flex: 1;
    min-width: 0;
    overflow: auto;
    white-space: nowrap;
    font-size: var(--font-size-xs, 12px);
    scrollbar-width: thin;
    span {
        color: var(--color-muted-foreground);
    }
    @media (max-width: 700px) {
        span {
            display: none;
        }
    }
`;
export const Columns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    flex: 1;
    min-height: 0;
    @media (max-width: 700px) {
        grid-template-columns: 1fr;
        overflow: hidden;
    }
`;
export const Pane = styled.section`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: auto;
    &:first-child {
        border-right: 1px solid var(--color-border);
    }
    @media (max-width: 700px) {
        overflow: auto;
        min-height: 0;
        &:first-child {
            border-right: 0;
            border-bottom: 1px solid var(--color-border);
        }
    }
`;
export const PaneHeading = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2, 8px);
    box-sizing: border-box;
    height: 46px;
    padding: 0 var(--space-4, 16px);
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
    h3 {
        margin: 0;
        font-size: var(--font-size-sm, 13px);
        font-weight: var(--font-weight-semibold, 600);
    }
    small {
        color: var(--color-muted-foreground);
        font-size: var(--font-size-xs, 11px);
    }
`;
export const SectionItem = styled(AccordionItem)`
    && {
        border-bottom: 1px solid var(--color-border);
    }
    &&[data-surface='muted'] [data-slot='accordion-content'] > div {
        padding-bottom: 0;
    }
`;
export const SectionTrigger = styled(AccordionTrigger)`
    && {
        padding: var(--space-3, 12px) var(--space-4, 16px);
        border-radius: 0;
        align-items: center;
        gap: var(--space-2, 8px);
        font-size: var(--font-size-xs, 12px);
    }
    &&[data-surface='muted'] {
        padding: var(--space-2, 8px) var(--space-3, 12px);
    }
    &&[data-surface='muted'],
    &&[data-surface='muted']:hover,
    &&:hover {
        text-decoration: none;
        background: var(--color-muted);
    }
    > span {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2, 8px);
    }
    small {
        color: var(--color-muted-foreground);
        font-weight: 400;
    }
`;
export const SessionHeaderTable = styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: var(--font-size-xs, 12px);
    tr:last-child th,
    tr:last-child td {
        border-bottom: 0;
    }
`;
export const SessionHeaderKeyColumn = styled.col`
    width: 132px;
`;
export const SessionHeaderKey = styled.th`
    width: 132px;
    padding: var(--space-1, 4px) var(--space-3, 12px);
    border-right: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    color: var(--color-foreground);
    font-weight: 500;
    text-align: left;
    vertical-align: middle;
`;
export const SessionHeaderValueCell = styled.td`
    padding: 0;
    border-bottom: 1px solid var(--color-border);
    vertical-align: middle;
`;
export const HeaderValueInput = styled.input`
    display: block;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: var(--space-1, 4px) var(--space-3, 12px);
    border: 0;
    border-radius: 0;
    background: transparent;
    color: var(--color-foreground);
    font: inherit;
    font-size: var(--font-size-xs, 12px);
    line-height: 1.5;
    &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 0 1px var(--color-ring);
    }
`;
export const Note = styled.p`
    margin: 0;
    padding: var(--space-3, 12px) var(--space-4, 16px);
    color: var(--color-muted-foreground);
    font-size: var(--font-size-xs, 12px);
    line-height: 1.6;
`;
export const ParameterTable = styled.div`
    display: grid;
    gap: var(--space-3, 12px);
    padding: 0 var(--space-4, 16px) var(--space-4, 16px);
`;
export const ParameterRow = styled.div`
    display: grid;
    grid-template-columns: minmax(100px, 0.8fr) minmax(100px, 1fr);
    align-items: start;
    column-gap: var(--space-3, 12px);
    row-gap: var(--space-1, 4px);
    > label {
        padding-top: var(--space-2, 8px);
        font-size: var(--font-size-xs, 12px);
        overflow-wrap: anywhere;
    }
    label small {
        display: block;
        color: var(--color-muted-foreground);
        font-size: var(--font-size-xs, 11px);
        font-weight: var(--font-weight-normal, 400);
    }
    > p {
        grid-column: 1 / -1;
        font-size: var(--font-size-xs, 11px);
        color: var(--color-muted-foreground);
        margin: 0;
        line-height: 1.5;
    }
    input,
    button[role='combobox'] {
        height: 34px;
        font-size: var(--font-size-xs, 12px);
    }
`;
export const ResponseMeta = styled.div`
    display: flex;
    align-items: center;
    gap: var(--space-2, 8px);
    font-size: var(--font-size-xs, 11px);
    color: var(--color-muted-foreground);
`;
const statusTone = {
    informational: css`
        color: var(--micro-app-reference-get-fg, #0550ae);
        background: var(--micro-app-reference-get-bg, #ddf4ff);
    `,
    success: css`
        color: var(--micro-app-reference-post-fg, #116329);
        background: var(--micro-app-reference-post-bg, #dafbe1);
    `,
    redirect: css`
        color: var(--micro-app-reference-get-fg, #0550ae);
        background: var(--micro-app-reference-get-bg, #ddf4ff);
    `,
    clientError: css`
        color: var(--micro-app-reference-patch-fg, #9a6700);
        background: var(--micro-app-reference-patch-bg, #fff8c5);
    `,
    serverError: css`
        color: var(--micro-app-reference-delete-fg, #cf222e);
        background: var(--micro-app-reference-delete-bg, #ffebe9);
    `,
};
export const StatusBadge = styled.span<{ $tone: StatusTone }>`
    display: inline-flex;
    align-items: center;
    padding: 2px var(--space-2, 8px);
    border-radius: var(--radius-sm, 6px);
    font-size: var(--font-size-xs, 11px);
    font-weight: var(--font-weight-bold, 700);
    line-height: 1.4;
    ${({ $tone }) => statusTone[$tone]}
`;
export const ResponseColumn = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
`;
export const ResponseBodyFill = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    > [data-slot='accordion'],
    [data-slot='accordion-item'],
    [data-slot='accordion-content'][data-state='open'],
    [data-slot='accordion-content'] > div {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-height: 0;
    }
    [data-slot='accordion-content'][data-state='open'] {
        height: auto;
        animation: none;
    }
    [data-slot='accordion-content'] > div {
        padding-bottom: 0;
    }
`;
export const ResponseJson = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    > div {
        flex: 1;
        min-height: 0;
    }
`;
export const TextResponse = styled.pre`
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    flex: 1;
    min-height: 0;
    padding: var(--space-4, 16px);
    margin: 0;
    font-size: var(--font-size-xs, 12px);
    line-height: 1.6;
    overflow: auto;
`;
export const EmptyResponse = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    text-align: center;
    padding: var(--space-5, 20px);
    color: var(--color-muted-foreground);
    gap: var(--space-2, 8px);
    > svg {
        padding: 12px;
        width: 48px;
        height: 48px;
        border: 1px solid var(--color-border);
        border-radius: var(--radius-xl, 12px);
        background: var(--color-muted);
        margin-bottom: 4px;
    }
    h4 {
        margin: 0;
        font-size: var(--font-size-sm, 13px);
        color: var(--color-foreground);
        font-weight: 500;
    }
    p {
        margin: 0;
        font-size: var(--font-size-xs, 12px);
        line-height: 1.6;
        max-width: 260px;
    }
`;
export const ErrorNotice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: var(--space-2, 8px);
    margin: var(--space-4, 16px);
    padding: var(--space-3, 12px);
    border: 1px solid color-mix(in srgb, var(--color-destructive) 25%, var(--color-border));
    border-radius: var(--radius-lg, 8px);
    color: var(--color-destructive);
    background: color-mix(in srgb, var(--color-destructive) 5%, var(--color-card));
    font-size: var(--font-size-xs, 12px);
    line-height: 1.6;
    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;
export const CompactResponseError = styled.div`
    display: flex;
    align-items: center;
    gap: var(--space-1, 4px);
    padding: var(--space-1, 4px) var(--space-3, 12px);
    border-bottom: 1px solid color-mix(in srgb, var(--color-destructive) 20%, var(--color-border));
    color: var(--color-destructive);
    background: color-mix(in srgb, var(--color-destructive) 4%, var(--color-muted));
    font-size: var(--font-size-xs, 11px);
    line-height: 1.4;
    svg {
        flex-shrink: 0;
    }
`;
export const PlaygroundBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;
export const SkeletonBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;
export const SkeletonPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--space-3, 12px);
    min-width: 0;
    padding: var(--space-4, 16px);
    &:first-child {
        border-right: 1px solid var(--color-border);
    }
`;
export const PlaygroundFooter = styled.footer`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--space-2, 8px);
    align-items: center;
    padding: var(--space-3, 12px) var(--space-5, 20px);
    border-top: 1px solid var(--color-border);
    font-size: var(--font-size-xs, 11px);
    color: var(--color-muted-foreground);
    span {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2, 8px);
    }
    kbd {
        border: 1px solid var(--color-border);
        border-radius: var(--radius-sm, 4px);
        background: var(--color-muted);
        padding: 1px 4px;
        font-size: var(--font-size-2xs, 10px);
    }
`;

export const PaneTabs = styled(Tabs)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-top: 1px solid var(--color-border);
`;
export const PaneTabList = styled(TabsList)`
    && {
        display: none;
    }
    @media (max-width: 700px) {
        && {
            display: flex;
            margin: var(--space-2, 8px) var(--space-4, 16px);
            height: 34px;
        }
        > button {
            flex: 1;
            font-size: var(--font-size-xs, 12px);
        }
    }
`;
export const PaneTabContent = styled(TabsContent)`
    && {
        display: flex;
        margin: 0;
        min-width: 0;
        min-height: 0;
    }
    @media (max-width: 700px) {
        &&[data-state='inactive'] {
            display: none;
        }
    }
`;
